import { categories, movies, type Movie } from '@/mocks/movies';
import { newsList, type NewsArticle } from '@/mocks/news';

export type SortKey = 'latest' | 'popular' | 'rating';

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新' },
  { key: 'popular', label: '最热' },
  { key: 'rating', label: '评分' },
];

export const regionOptions = ['全部', '日本', '韩国'];

export const yearOptions = ['全部', '2025', '2024', '2023', '2022 及更早'];

export const durationOptions = ['全部', '90 分钟以内', '90 - 120 分钟', '120 分钟以上'];

export function getCategory(slug: string) {
  return categories.find((item) => item.slug === slug);
}

export function getMovieById(id: string) {
  return movies.find((item) => item.id === id);
}

export function getNewsById(id: string) {
  return newsList.find((item) => item.id === id);
}

export function getCategoryName(slug: string) {
  return getCategory(slug)?.name ?? slug;
}

export function getCategoryCount(slug: string) {
  return movies.filter((item) => item.category === slug).length;
}

export function getTagCount(tag: string) {
  return movies.filter((item) => item.tags.includes(tag)).length;
}

export function formatViews(views: number) {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}

export function formatDate(date: string) {
  return date.replace(/-/g, '.');
}

type FilterArgs = {
  category?: string;
  tag?: string;
  region?: string;
  year?: string;
  duration?: string;
  sort?: SortKey;
};

export function filterMovies({ category, tag, region, year, duration, sort = 'latest' }: FilterArgs): Movie[] {
  let list = [...movies];

  if (category) {
    list = list.filter((item) => item.category === category);
  }
  if (tag) {
    list = list.filter((item) => item.tags.includes(tag));
  }
  if (region && region !== '全部') {
    list = list.filter((item) => item.region === region);
  }
  if (year && year !== '全部') {
    if (year === '2022 及更早') {
      list = list.filter((item) => item.year <= 2022);
    } else {
      list = list.filter((item) => String(item.year) === year);
    }
  }
  if (duration && duration !== '全部') {
    if (duration === '90 分钟以内') {
      list = list.filter((item) => item.duration <= 90);
    } else if (duration === '90 - 120 分钟') {
      list = list.filter((item) => item.duration > 90 && item.duration <= 120);
    } else if (duration === '120 分钟以上') {
      list = list.filter((item) => item.duration > 120);
    }
  }

  if (sort === 'popular') {
    list.sort((a, b) => b.views - a.views);
  } else if (sort === 'rating') {
    list.sort((a, b) => b.rating - a.rating);
  } else {
    list.sort((a, b) => b.year - a.year || b.views - a.views);
  }

  return list;
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.category === movie.category) score += 3;
      score += item.tags.filter((tag) => movie.tags.includes(tag)).length * 2;
      if (item.region === movie.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);

  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year || b.views - a.views).slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getTopRatedMovies(limit = 4): Movie[] {
  return [...movies].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getLatestNews(limit = 3): NewsArticle[] {
  return [...newsList].sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit);
}

function matchText(text: string, keyword: string) {
  return text.toLowerCase().includes(keyword.toLowerCase());
}

export function searchMovies(keyword: string): Movie[] {
  const key = keyword.trim();
  if (!key) return [];
  return movies.filter((item) =>
    [item.title, item.originalTitle, item.englishTitle, item.director, item.category, ...item.cast, ...item.tags, item.summary].some(
      (field) => matchText(field, key),
    ),
  );
}

export function searchNews(keyword: string): NewsArticle[] {
  const key = keyword.trim();
  if (!key) return [];
  return newsList.filter((item) =>
    [item.title, item.section, item.summary, item.author, ...item.tags].some((field) => matchText(field, key)),
  );
}

export function highlightParts(text: string, keyword: string): { text: string; hit: boolean }[] {
  const key = keyword.trim();
  if (!key) return [{ text, hit: false }];
  const lower = text.toLowerCase();
  const lowerKey = key.toLowerCase();
  const parts: { text: string; hit: boolean }[] = [];
  let cursor = 0;
  let index = lower.indexOf(lowerKey);
  while (index !== -1) {
    if (index > cursor) {
      parts.push({ text: text.slice(cursor, index), hit: false });
    }
    parts.push({ text: text.slice(index, index + key.length), hit: true });
    cursor = index + key.length;
    index = lower.indexOf(lowerKey, cursor);
  }
  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), hit: false });
  }
  return parts.length ? parts : [{ text, hit: false }];
}

export function getTagList(): { name: string; count: number }[] {
  const map = new Map<string, number>();
  movies.forEach((movie) => {
    movie.tags.forEach((tag) => {
      map.set(tag, (map.get(tag) ?? 0) + 1);
    });
  });
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}